import React, { useState, useEffect } from 'react';
import { BubbleMenu } from '@tiptap/react/menus'
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Image as ImageIcon, 
  X,
  Trash2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Edit
} from 'lucide-react';
import type { Editor } from '@tiptap/react';
import { cn } from '@/lib/utils';
import { ImageEditorModal } from './ImageEditorModal';

interface ImageBubbleMenuProps {
  editor: Editor;
}

export const ImageBubbleMenu: React.FC<ImageBubbleMenuProps> = ({ editor }) => {
  const [showProperties, setShowProperties] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [alt, setAlt] = useState('');
  const [width, setWidth] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [href, setHref] = useState('');
  const [align, setAlign] = useState<string>('');
  const [currentImageSrc, setCurrentImageSrc] = useState<string>('');

  // Update form when image selection changes
  useEffect(() => {
    if (!editor) return;

    const updateForm = () => {
      if (editor.isActive('image')) {
        const attrs = editor.getAttributes('image');
        setAlt(attrs.alt || '');
        setWidth(attrs.width ? String(attrs.width) : '');
        setHeight(attrs.height ? String(attrs.height) : '');
        setHref(attrs.href || '');
        setAlign(attrs.align || '');
        setCurrentImageSrc(attrs.src || '');
      }
    };

    // Update immediately
    updateForm();

    // Listen for selection updates
    editor.on('selectionUpdate', updateForm);
    editor.on('update', updateForm);

    return () => {
      editor.off('selectionUpdate', updateForm);
      editor.off('update', updateForm);
    };
  }, [editor]);

  const handleUpdateImage = () => {
    if (!editor) return;

    const currentAttrs = editor.getAttributes('image');
    const attrs: Record<string, unknown> = {
      src: currentAttrs.src, // Keep the current src
      alt: alt || null,
      width: width ? parseInt(width, 10) : null,
      height: height ? parseInt(height, 10) : null,
      href: href || null,
      align: align || null,
    };

    // Remove null/empty values
    Object.keys(attrs).forEach(key => {
      if (attrs[key] === null || attrs[key] === '') {
        delete attrs[key];
      }
    });

    editor.chain().focus().setImage(attrs as { src: string }).run();
    setShowProperties(false);
  };

  const handleDeleteImage = () => {
    if (!editor) return;
    editor.chain().focus().deleteSelection().run();
  };

  const handleRemoveLink = () => {
    setHref('');
    if (editor) {
      const currentAttrs = editor.getAttributes('image');
      const newAttrs: Record<string, unknown> = { ...currentAttrs };
      delete newAttrs.href;
      editor.chain().focus().setImage(newAttrs as { src: string }).run();
    }
  };

  const handleAlignChange = (newAlign: 'left' | 'center' | 'right' | null) => {
    if (!editor) return;
    const currentAttrs = editor.getAttributes('image');
    const updatedAttrs: Record<string, unknown> = { 
      ...currentAttrs, 
      align: newAlign 
    };
    editor.chain().focus().setImage(updatedAttrs as { src: string }).run();
  };

  const handleOpenEditor = () => {
    if (!editor) return;
    const attrs = editor.getAttributes('image');
    if (attrs.src) {
      setCurrentImageSrc(attrs.src);
      setShowEditor(true);
    }
  };

  const handleSaveEditedImage = (newImageUrl: string) => {
    if (!editor) return;
    const currentAttrs = editor.getAttributes('image');
    const updatedAttrs: Record<string, unknown> = {
      ...currentAttrs,
      src: newImageUrl,
    };
    editor.chain().focus().setImage(updatedAttrs as { src: string }).run();
  };

  if (!editor) return null;

  return (
    <BubbleMenu
      className="bubble-menu"
      editor={editor}
      shouldShow={({ editor }) => {
        return editor.isActive('image');
      }}
    >
      {showProperties ? (
        <div className="bg-background border rounded-lg shadow-lg p-4 min-w-[300px] z-[9999]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm">Image Properties</h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setShowProperties(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-3">
            {/* Alt Text */}
            <div className="space-y-1">
              <Label htmlFor="bubble-alt" className="text-xs">Alt Text</Label>
              <Input
                id="bubble-alt"
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                placeholder="Describe the image"
                className="h-8 text-sm"
              />
            </div>

            {/* Dimensions */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="bubble-width" className="text-xs">Width (px)</Label>
                <Input
                  id="bubble-width"
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="Auto"
                  className="h-8 text-sm"
                  min="1"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="bubble-height" className="text-xs">Height (px)</Label>
                <Input
                  id="bubble-height"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="Auto"
                  className="h-8 text-sm"
                  min="1"
                />
              </div>
            </div>

            {/* Link */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="bubble-link" className="text-xs">Link URL</Label>
                {href && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLink}
                    className="h-5 px-2 text-xs"
                  >
                    Remove
                  </Button>
                )}
              </div>
              <Input
                id="bubble-link"
                type="url"
                value={href}
                onChange={(e) => setHref(e.target.value)}
                placeholder="https://example.com"
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteImage}
              className="flex-1"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Delete
            </Button>
            <Button
              size="sm"
              onClick={handleUpdateImage}
              className="flex-1"
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-background border rounded-lg shadow-lg p-1 flex items-center gap-1 z-[9999]">
          {(['left', 'center', 'right'] as const).map((alignment) => {
            const Icon = alignment === 'left' ? AlignLeft : alignment === 'center' ? AlignCenter : AlignRight;
            const isActive = align === alignment;
            return (
              <Button
                key={alignment}
                variant="ghost"
                size="sm"
                className={cn("h-8 px-2", isActive && "bg-accent")}
                onClick={() => handleAlignChange(isActive ? null : alignment)}
                title={`Align ${alignment.charAt(0).toUpperCase() + alignment.slice(1)}`}
              >
                <Icon className="h-4 w-4" />
              </Button>
            );
          })}
          <div className="w-px h-6 bg-border mx-1" />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenEditor();
            }}
            title="Edit Image"
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => setShowProperties(true)}
            title="Edit Properties"
          >
            <ImageIcon className="h-4 w-4 mr-1" />
            Properties
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={handleDeleteImage}
            title="Delete Image"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
      <ImageEditorModal
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        imageSrc={currentImageSrc}
        onSave={handleSaveEditedImage}
      />
    </BubbleMenu>
  );
};

